/**
 * AES-256-GCM Encryption Utility (Frontend)
 *
 * Uses the Web Crypto API (SubtleCrypto) — zero npm dependencies.
 * Encryption is ONLY active in production when REACT_APP_ENCRYPTION_KEY is set.
 *
 * The shared secret is hashed with SHA-256 to produce a fixed 256-bit key.
 * Each encryption uses a random 12-byte IV (nonce) for GCM security.
 */

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || '';

/**
 * Web Crypto (`crypto.subtle`) only exists in secure contexts, and is missing
 * or restricted in several in-app browsers candidates commonly open apply links
 * from (WhatsApp/Instagram/Facebook webviews) and on any non-HTTPS origin.
 * Without this guard those candidates hit a hard failure on every API call
 * instead of simply falling back to plaintext over TLS.
 */
export function isCryptoAvailable() {
  return (
    typeof window !== 'undefined' &&
    !!window.crypto &&
    !!window.crypto.subtle &&
    typeof window.crypto.subtle.digest === 'function' &&
    typeof window.crypto.getRandomValues === 'function'
  );
}

/**
 * Returns true when encryption should be used.
 * Conditions: production build AND the env var is set AND Web Crypto works here.
 */
export function isEncryptionEnabled() {
  return (
    process.env.NODE_ENV === 'production' &&
    ENCRYPTION_KEY.length > 0 &&
    isCryptoAvailable()
  );
}

// ─── Internal helpers ────────────────────────────────────────────────

let _cachedKey = null;

/**
 * Derive a CryptoKey from the shared secret using SHA-256.
 */
async function getKey() {
  if (_cachedKey) return _cachedKey;

  const encoder = new TextEncoder();
  const keyMaterial = encoder.encode(ENCRYPTION_KEY);

  // SHA-256 hash → 32 bytes
  const rawKey = await window.crypto.subtle.digest('SHA-256', keyMaterial);

  _cachedKey = await window.crypto.subtle.importKey(
    'raw',
    rawKey,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );

  return _cachedKey;
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// ─── Public API ──────────────────────────────────────────────────────

/**
 * Encrypt a JavaScript object/value into an encrypted payload.
 * @param {*} data - The data to encrypt (will be JSON-stringified)
 * @returns {Promise<{ iv: string, data: string, tag: string }>}
 */
export async function encryptPayload(data) {
  const key = await getKey();

  const encoder = new TextEncoder();
  const plaintext = encoder.encode(JSON.stringify(data));

  // 12-byte random IV (recommended for GCM)
  const iv = window.crypto.getRandomValues(new Uint8Array(12));

  const ciphertext = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    key,
    plaintext
  );

  // Web Crypto appends the 16-byte auth tag at the end of the ciphertext
  const ctBytes = new Uint8Array(ciphertext);
  const encryptedData = ctBytes.slice(0, ctBytes.length - 16);
  const tag = ctBytes.slice(ctBytes.length - 16);

  return {
    iv: arrayBufferToBase64(iv),
    data: arrayBufferToBase64(encryptedData),
    tag: arrayBufferToBase64(tag),
  };
}

/**
 * Decrypt an encrypted payload back to the original data.
 * @param {{ iv: string, data: string, tag: string }} payload
 * @returns {Promise<*>} The decrypted, JSON-parsed data
 */
export async function decryptPayload(payload) {
  const key = await getKey();

  const iv = new Uint8Array(base64ToArrayBuffer(payload.iv));
  const encryptedData = new Uint8Array(base64ToArrayBuffer(payload.data));
  const tag = new Uint8Array(base64ToArrayBuffer(payload.tag));

  // Reconstruct ciphertext + tag (Web Crypto expects them concatenated)
  const combined = new Uint8Array(encryptedData.length + tag.length);
  combined.set(encryptedData, 0);
  combined.set(tag, encryptedData.length);

  const plaintext = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    key,
    combined
  );

  const decoder = new TextDecoder();
  return JSON.parse(decoder.decode(plaintext));
}
